import {
  ALL_AREAS_LABEL,
  AREA_FILTER_MODE,
  AppliedFilter,
  AppliedFilterApiItem,
  AreaFilterMode,
  AreaSelection,
  INACTIVE_AREA_SUFFIX,
  INCIDENT_SCOPE,
  INCIDENT_SCOPE_LABEL,
  MY_AREAS_LABEL,
  MY_AREA_SUFFIX,
  MeScope,
  SCOPE_AREAS_VALUE,
  SCOPE_MENU_VALUE,
  ScopeArea,
  SCOPE_ACCESS,
  ScopeAccess,
  ScopeAreaApiItem,
  ScopeFieldsApiItem,
  ScopeMenuOption,
  ScopeMenuOptions,
  ScopeWarehouse,
  ScopeWarehouseApiItem,
  buildDefaultAreaSelection,
} from '../models/scope.model';

export interface ScopeFields {
  area: ScopeArea | null;
  warehouse: ScopeWarehouse | null;
  isForeign: boolean;
}

const AREA_FILTER_MODE_VALUES: readonly string[] = Object.values(AREA_FILTER_MODE);

export function buildAreasParam(selection: AreaSelection): string | null {
  if (selection.mode === AREA_FILTER_MODE.ALL) return SCOPE_AREAS_VALUE.ALL;
  if (selection.mode === AREA_FILTER_MODE.CUSTOM && selection.areaIds.length > 0) {
    return selection.areaIds.join(',');
  }

  return null;
}

export function toScopeArea(raw: ScopeAreaApiItem | null | undefined): ScopeArea | null {
  if (!raw?.id) return null;

  return { id: raw.id, name: raw.name ?? '' };
}

export function toScopeWarehouse(
  raw: ScopeWarehouseApiItem | null | undefined,
): ScopeWarehouse | null {
  if (!raw?.code && !raw?.name) return null;

  return { code: raw?.code ?? '', name: raw?.name ?? '' };
}

export function toScopeAreas(raw: ScopeAreaApiItem[] | null | undefined): ScopeArea[] {
  if (!Array.isArray(raw)) return [];

  return raw.map((item) => toScopeArea(item)).filter((area): area is ScopeArea => area !== null);
}

export function toAppliedFilter(
  raw: AppliedFilterApiItem | null | undefined,
): AppliedFilter | null {
  if (!raw) return null;

  return {
    mode: toAreaFilterMode(raw.mode),
    warehouse: toScopeWarehouse(raw.warehouse),
    areas: toScopeAreas(raw.areas),
    label: raw.label ?? '',
  };
}

export function toScopeFields(raw: ScopeFieldsApiItem | null | undefined): ScopeFields {
  return {
    area: toScopeArea(raw?.area),
    warehouse: toScopeWarehouse(raw?.warehouse),
    isForeign: raw?.is_foreign ?? false,
  };
}

export function toScopeAccess(raw: string | null | undefined): ScopeAccess {
  return raw === SCOPE_ACCESS.READ_ONLY ? SCOPE_ACCESS.READ_ONLY : SCOPE_ACCESS.EDIT;
}

export function getMyAreaIds(scope: MeScope | null): string[] {
  if (!scope) return [];

  const ids = new Set<string>(scope.areas.map((area) => area.id));
  scope.warehouseAreas.filter((area) => area.isMine).forEach((area) => ids.add(area.id));

  return [...ids];
}

export function normalizeAreaSelection(areaIds: string[], scope: MeScope | null): AreaSelection {
  if (areaIds.length === 0 || isSameAreaSet(areaIds, getMyAreaIds(scope))) {
    return buildDefaultAreaSelection();
  }

  return { mode: AREA_FILTER_MODE.CUSTOM, areaIds: [...areaIds] };
}

export function buildScopeMenuOptions(
  scope: MeScope | null,
  selection: AreaSelection,
): ScopeMenuOptions {
  if (!scope) return { options: [], selectedValue: null };

  const myAreaIds = getMyAreaIds(scope);
  const options: ScopeMenuOption[] = [
    { value: SCOPE_MENU_VALUE.ALL, label: ALL_AREAS_LABEL, suffix: null },
  ];

  if (myAreaIds.length > 1) {
    options.push({ value: SCOPE_MENU_VALUE.DEFAULT, label: MY_AREAS_LABEL, suffix: null });
  }

  scope.warehouseAreas.forEach((area) =>
    options.push({
      value: area.id,
      label: area.name,
      suffix: buildAreaSuffix(area.isMine, area.active),
    }),
  );

  return { options, selectedValue: resolveSelectedValue(selection, myAreaIds) };
}

export function toAreaSelection(menuValue: string, scope: MeScope | null): AreaSelection {
  if (menuValue === SCOPE_MENU_VALUE.ALL) {
    return { mode: AREA_FILTER_MODE.ALL, areaIds: [] };
  }

  if (menuValue === SCOPE_MENU_VALUE.DEFAULT) return buildDefaultAreaSelection();

  return normalizeAreaSelection([menuValue], scope);
}

export function resolveIncidentScopeLabel(scope: MeScope | null): string | null {
  if (!scope) return null;

  switch (scope.incidentScope) {
    case INCIDENT_SCOPE.AREA:
      return scope.defaultSelection.label || null;
    case INCIDENT_SCOPE.WAREHOUSE:
      return buildWarehouseIncidentLabel(scope.warehouse);
    case INCIDENT_SCOPE.ALL:
      return INCIDENT_SCOPE_LABEL.ALL;
    default:
      return null;
  }
}

export function formatForeignAreaLabel(
  area: ScopeArea | null,
  warehouse: ScopeWarehouse | null,
): string {
  const areaName = area?.name ?? '';
  const warehouseCode = warehouse?.code || warehouse?.name || '';

  if (!areaName) return warehouseCode;
  if (!warehouseCode) return areaName;

  return `${areaName} - ${warehouseCode}`;
}

export function isInAppliedAreas(
  area: ScopeArea | null,
  appliedFilter: AppliedFilter | null,
): boolean {
  if (!area || !appliedFilter || appliedFilter.areas.length === 0) return true;

  return appliedFilter.areas.some((appliedArea) => appliedArea.id === area.id);
}

function toAreaFilterMode(raw: string | null | undefined): AreaFilterMode {
  if (raw && AREA_FILTER_MODE_VALUES.includes(raw)) return raw as AreaFilterMode;

  return AREA_FILTER_MODE.DEFAULT;
}

function isSameAreaSet(left: string[], right: string[]): boolean {
  if (left.length === 0 || left.length !== right.length) return false;

  const rightSet = new Set(right);

  return left.every((id) => rightSet.has(id));
}

function buildAreaSuffix(isMine: boolean, isActive: boolean): string | null {
  const parts: string[] = [];

  if (isMine) parts.push(MY_AREA_SUFFIX);
  if (!isActive) parts.push(INACTIVE_AREA_SUFFIX);

  return parts.length > 0 ? parts.join(', ') : null;
}

function resolveSelectedValue(selection: AreaSelection, myAreaIds: string[]): string | null {
  if (selection.mode === AREA_FILTER_MODE.ALL) return SCOPE_MENU_VALUE.ALL;

  if (selection.mode === AREA_FILTER_MODE.CUSTOM) {
    return selection.areaIds.length === 1 ? selection.areaIds[0] : null;
  }

  if (myAreaIds.length > 1) return SCOPE_MENU_VALUE.DEFAULT;

  return myAreaIds[0] ?? null;
}

function buildWarehouseIncidentLabel(warehouse: ScopeWarehouse | null): string {
  const warehouseName = warehouse?.name || warehouse?.code;

  return warehouseName
    ? `${INCIDENT_SCOPE_LABEL.WAREHOUSE} - ${warehouseName}`
    : INCIDENT_SCOPE_LABEL.WAREHOUSE;
}
